import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { getCloudflareAnalytics } from '../services/cloudflareAnalytics';
import schedulesData from '../data/schedules.json';
import stagesData from '../data/stages.json';
import type { AppSettings, DaySchedule, Performance, Stage } from '../types';
import i18n, { normalizeLanguage } from '../i18n';

interface ScheduleContextType {
  schedules: DaySchedule[];
  stages: Stage[];
  allPerformances: Performance[];
  favorites: string[]; // DJ names
  settings: AppSettings;
  virtualTime: Date;
  toggleFavorite: (djName: string) => void;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
  getVirtualTime: () => Date;
}

const ScheduleContext = createContext<ScheduleContextType | undefined>(undefined);

// Helper to flatten schedules
const flattenSchedules = (data: any[]): Performance[] => {
  const all: Performance[] = [];
  data.forEach((daySchedule: any) => {
    daySchedule.schedule.forEach((stageSchedule: any) => {
      stageSchedule.performances.forEach((perf: any) => {
        all.push({
          ...perf,
          stage: stageSchedule.stage,
          day: daySchedule.day,
        });
      });
    });
  });
  return all;
};

export const ScheduleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const FAVORITES_KEY = 'ravepal_favorites';
  const SETTINGS_KEY = 'ravepal_settings';
  const isDev = import.meta.env.DEV;

  const [favorites, setFavorites] = useState<string[]>(() => {
    let saved = localStorage.getItem(FAVORITES_KEY);
    if (!saved) {
      const legacy = localStorage.getItem('edc_favorites');
      if (legacy) {
        localStorage.setItem(FAVORITES_KEY, legacy);
        localStorage.removeItem('edc_favorites');
        saved = legacy;
      }
    }
    return saved ? JSON.parse(saved) : [];
  });

  const [settings, setSettings] = useState<AppSettings>(() => {
    let saved = localStorage.getItem(SETTINGS_KEY);
    if (!saved) {
      const legacy = localStorage.getItem('edc_settings');
      if (legacy) {
        localStorage.setItem(SETTINGS_KEY, legacy);
        localStorage.removeItem('edc_settings');
        saved = legacy;
      }
    }
    const parsed = saved
      ? JSON.parse(saved)
      : {
          simulationTime: null,
          notificationBefore: 15,
          language: normalizeLanguage(typeof navigator !== 'undefined' ? navigator.language : 'en'),
        };
    if (parsed.notificationBefore === undefined) {
        parsed.notificationBefore = 15;
    }
    if (!parsed.language) {
        parsed.language = normalizeLanguage(typeof navigator !== 'undefined' ? navigator.language : 'en');
    } else {
        parsed.language = normalizeLanguage(parsed.language);
    }
    if (parsed.simulationTime) {
        parsed.simulationTime = new Date(parsed.simulationTime);
    }
    return parsed;
  });
  const effectiveSimulationTime = isDev ? settings.simulationTime : null;

  const [virtualTime, setVirtualTime] = useState<Date>(new Date());

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    const nextLanguage = normalizeLanguage(settings.language);
    if (i18n.language !== nextLanguage) {
      i18n.changeLanguage(nextLanguage);
    }
  }, [settings.language]);

  // Clock ticker for virtual time or real time
  useEffect(() => {
    const interval = setInterval(() => {
      if (effectiveSimulationTime) {
        setVirtualTime(effectiveSimulationTime);
      } else {
        setVirtualTime(new Date());
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [effectiveSimulationTime]);

  const toggleFavorite = useCallback((djName: string) => {
    setFavorites(prev => {
      const isAdding = !prev.includes(djName);
      getCloudflareAnalytics().trackEvent(
        isAdding ? 'favorite_added' : 'favorite_removed',
        { dj: djName }
      );
      if (prev.includes(djName)) {
        return prev.filter(n => n !== djName);
      }
      return [...prev, djName];
    });
  }, []);

  const updateSettings = useCallback((newSettings: Partial<AppSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  const getVirtualTime = useCallback(() => {
    return effectiveSimulationTime || new Date();
  }, [effectiveSimulationTime]);

  const allPerformances = React.useMemo(() => flattenSchedules(schedulesData), []);

  const value = {
    schedules: schedulesData as unknown as DaySchedule[],
    stages: stagesData as unknown as Stage[],
    allPerformances,
    favorites,
    settings,
    virtualTime,
    toggleFavorite,
    updateSettings,
    getVirtualTime,
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
};

export const useSchedule = () => {
  const context = useContext(ScheduleContext);
  if (context === undefined) {
    throw new Error('useSchedule must be used within a ScheduleProvider');
  }
  return context;
};
